import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const SKILL_COLORS = {
  grammar:    { bg: '#1D4ED8', light: '#EFF6FF', text: '#1E3A8A' },
  vocabulary: { bg: '#16A34A', light: '#F0FDF4', text: '#14532D' },
  writing:    { bg: '#C2410C', light: '#FFF7ED', text: '#7C2D12' },
  reading:    { bg: '#7C3AED', light: '#F5F3FF', text: '#4C1D95' },
  speaking:   { bg: '#92400E', light: '#FEF9C3', text: '#78350F' },
};

// ─── Slide builders ──────────────────────────────────────────────────────────
function buildSlides(mat) {
  const c = mat.content || {};
  const slides = [];
  const sc = SKILL_COLORS[mat.skill_type] || SKILL_COLORS.grammar;

  // Slide 0 — Title
  slides.push({ type: 'title', mat, sc });

  if (mat.skill_type === 'grammar') {
    if (c.explanation)
      slides.push({ type: 'explanation', text: c.explanation, sc, title: mat.title });
    if (c.common_mistakes)
      slides.push({ type: 'mistakes', text: c.common_mistakes, sc, title: mat.title });
    if (c.examples && c.examples.length)
      slides.push({ type: 'examples', items: c.examples, sc, title: mat.title });
    if (c.exercises && c.exercises.length)
      c.exercises.forEach((ex, i) =>
        slides.push({ type: 'exercise', ex, idx: i, total: c.exercises.length, sc, title: mat.title })
      );
  }

  if (mat.skill_type === 'vocabulary') {
    if (c.words && c.words.length)
      c.words.forEach((w, i) =>
        slides.push({ type: 'word', word: w, idx: i, total: c.words.length, sc, title: mat.title })
      );
    if (c.exercises && c.exercises.length)
      c.exercises.forEach((ex, i) =>
        slides.push({ type: 'exercise', ex, idx: i, total: c.exercises.length, sc, title: mat.title })
      );
  }

  if (mat.skill_type === 'writing') {
    if (c.explanation)
      slides.push({ type: 'explanation', text: c.explanation, sc, title: mat.title });
    if (c.prompt)
      slides.push({ type: 'writing_prompt', prompt: c.prompt, requirements: c.requirements, sc, title: mat.title });
    if (c.model_text)
      slides.push({ type: 'model_text', text: c.model_text, sc, title: mat.title });
    if (c.useful_phrases && c.useful_phrases.length)
      slides.push({ type: 'phrases', items: c.useful_phrases, sc, title: mat.title });
  }

  if (mat.skill_type === 'reading') {
    if (c.passage)
      slides.push({ type: 'passage', text: c.passage, sc, title: mat.title });
    if (c.questions && c.questions.length)
      slides.push({ type: 'comprehension', items: c.questions, sc, title: mat.title });
    if (c.vocabulary_in_context && c.vocabulary_in_context.length)
      slides.push({ type: 'vocab_context', items: c.vocabulary_in_context, sc, title: mat.title });
  }

  // Wrap-up
  slides.push({ type: 'wrapup', mat, sc });
  return slides;
}

// ─── Individual slide renderers ───────────────────────────────────────────────
function SlideTitle({ slide }) {
  const { mat, sc } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:24, textAlign:'center', padding:'0 60px' }}>
      <div style={{ width:80, height:80, borderRadius:20, background:sc.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:36 }}>
        {{ grammar:'📖', vocabulary:'📚', writing:'✍️', reading:'📄', speaking:'🗣️' }[mat.skill_type] || '📖'}
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:2, color:sc.bg, marginBottom:12 }}>
          {mat.skill_type} · {mat.cefr_level}
        </div>
        <div style={{ fontSize:52, fontWeight:800, color:'#0D1B2A', lineHeight:1.15, marginBottom:16 }}>{mat.title}</div>
        {mat.theme && <div style={{ fontSize:20, color:'#6B7280', fontStyle:'italic' }}>{mat.theme}</div>}
        {mat.description && <div style={{ fontSize:17, color:'#374151', marginTop:16, maxWidth:600, lineHeight:1.6 }}>{mat.description}</div>}
      </div>
    </div>
  );
}

function SlideExplanation({ slide }) {
  const { text, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>📖 Explanation</div>
      <div style={{ flex:1, display:'flex', alignItems:'center' }}>
        <div style={{ fontSize:26, lineHeight:1.7, color:'#1F2937', background:sc.light, borderRadius:16, padding:'32px 40px', width:'100%', borderLeft:`6px solid ${sc.bg}` }}>
          {text}
        </div>
      </div>
    </div>
  );
}

function SlideMistakes({ slide }) {
  const { text, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>⚠️ Common Mistakes</div>
      <div style={{ flex:1, display:'flex', alignItems:'center' }}>
        <div style={{ fontSize:24, lineHeight:1.7, color:'#1F2937', background:'#FEF3C7', borderRadius:16, padding:'32px 40px', width:'100%', borderLeft:'6px solid #F59E0B' }}>
          {text}
        </div>
      </div>
    </div>
  );
}

function SlideExamples({ slide }) {
  const { items, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>✏️ Examples</div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:14 }}>
        {items.map((ex, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:16, background:sc.light, borderRadius:12, padding:'16px 24px', borderLeft:`5px solid ${sc.bg}` }}>
            <div style={{ fontSize:22, fontWeight:800, color:sc.bg, minWidth:28 }}>{i + 1}</div>
            <div style={{ fontSize:24, color:'#1F2937', lineHeight:1.5, fontStyle:'italic' }}>{ex}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideExercise({ slide, showAnswer, onToggleAnswer }) {
  const { ex, idx, total, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>{title}</div>
          <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A' }}>🧩 Exercise {idx + 1} of {total}</div>
        </div>
        <div style={{ fontSize:14, color:'#9CA3AF', fontWeight:600 }}>Question {idx + 1} / {total}</div>
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:20 }}>
        <div style={{ background:'#F9FAFB', borderRadius:16, padding:'32px 40px', borderLeft:`6px solid ${sc.bg}` }}>
          <div style={{ fontSize:28, fontWeight:600, color:'#1F2937', lineHeight:1.5, marginBottom:20 }}>{ex.question}</div>
          {ex.options && ex.options.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {ex.options.map((opt, i) => (
                <div key={i} style={{
                  padding:'12px 20px', borderRadius:10, fontSize:20, fontWeight:500,
                  background: showAnswer && opt === ex.answer ? '#D1FAE5' : '#fff',
                  border: showAnswer && opt === ex.answer ? '2px solid #10B981' : '2px solid #E5E7EB',
                  color: showAnswer && opt === ex.answer ? '#065F46' : '#374151',
                }}>
                  {String.fromCharCode(65 + i)}. {opt}
                  {showAnswer && opt === ex.answer && <span style={{ marginLeft:12, fontWeight:700 }}>✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {showAnswer && ex.answer && !ex.options && (
          <div style={{ background:'#D1FAE5', borderRadius:12, padding:'20px 32px', border:'2px solid #10B981' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#065F46', marginBottom:4 }}>ANSWER</div>
            <div style={{ fontSize:26, fontWeight:700, color:'#065F46' }}>{ex.answer}</div>
            {ex.explanation && <div style={{ fontSize:18, color:'#374151', marginTop:8 }}>{ex.explanation}</div>}
          </div>
        )}
        <button
          onClick={onToggleAnswer}
          style={{
            alignSelf:'flex-start', padding:'12px 28px', borderRadius:10, border:'none',
            background: showAnswer ? '#6B7280' : sc.bg, color:'#fff',
            fontSize:16, fontWeight:700, cursor:'pointer'
          }}>
          {showAnswer ? 'Hide Answer' : 'Reveal Answer'}
        </button>
      </div>
    </div>
  );
}

function SlideWord({ slide }) {
  const { word, idx, total, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>{title}</div>
          <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A' }}>📚 Vocabulary Card {idx + 1} of {total}</div>
        </div>
      </div>
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'#fff', border:`3px solid ${sc.bg}`, borderRadius:20, padding:'48px 60px', textAlign:'center', maxWidth:600, width:'100%', boxShadow:'0 8px 32px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:48, fontWeight:800, color:'#0D1B2A', marginBottom:8 }}>{word.word}</div>
          {word.pronunciation && (
            <div style={{ fontSize:22, color:sc.bg, marginBottom:16, fontFamily:'monospace' }}>[{word.pronunciation}]</div>
          )}
          <div style={{ width:60, height:3, background:sc.bg, margin:'0 auto 20px' }} />
          <div style={{ fontSize:22, color:'#374151', marginBottom:20, lineHeight:1.5 }}>{word.definition}</div>
          {word.example && (
            <div style={{ background:sc.light, borderRadius:12, padding:'16px 24px' }}>
              <div style={{ fontSize:13, fontWeight:700, color:sc.bg, marginBottom:6, textTransform:'uppercase', letterSpacing:1 }}>Example</div>
              <div style={{ fontSize:20, color:'#1F2937', fontStyle:'italic', lineHeight:1.5 }}>"{word.example}"</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SlideWritingPrompt({ slide }) {
  const { prompt, requirements, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>✍️ Writing Task</div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:20 }}>
        <div style={{ background:'#FFF7ED', borderRadius:16, padding:'32px 40px', borderLeft:'6px solid #C2410C', fontSize:26, lineHeight:1.6, color:'#1F2937' }}>
          {prompt}
        </div>
        {requirements && requirements.length > 0 && (
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>Requirements</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {requirements.map((r, i) => (
                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, fontSize:20, color:'#374151' }}>
                  <span style={{ color:sc.bg, fontWeight:700, minWidth:20 }}>✓</span> {r}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SlideModelText({ slide }) {
  const { text, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>📝 Model Text</div>
      <div style={{ flex:1, display:'flex', alignItems:'center' }}>
        <div style={{ background:'#F9FAFB', borderRadius:16, padding:'32px 40px', width:'100%', borderLeft:`6px solid ${sc.bg}`, fontSize:22, lineHeight:1.8, color:'#1F2937' }}>
          {text}
        </div>
      </div>
    </div>
  );
}

function SlidePhrases({ slide }) {
  const { items, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>💬 Useful Phrases</div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:12 }}>
        {items.map((p, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:16, background:sc.light, borderRadius:12, padding:'16px 24px', borderLeft:`5px solid ${sc.bg}` }}>
            <div style={{ fontSize:20, fontWeight:800, color:sc.bg, minWidth:28 }}>{i + 1}</div>
            <div style={{ fontSize:22, color:'#1F2937', fontStyle:'italic' }}>{p}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlidePassage({ slide }) {
  const { text, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px', overflowY:'auto' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>📄 Reading Passage</div>
      <div style={{ background:'#F5F3FF', borderRadius:16, padding:'32px 40px', borderLeft:`6px solid ${sc.bg}`, fontSize:22, lineHeight:1.9, color:'#1F2937' }}>
        {text}
      </div>
    </div>
  );
}

function SlideComprehension({ slide }) {
  const { items, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>❓ Comprehension Questions</div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:14 }}>
        {items.map((q, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:16, background:'#F9FAFB', borderRadius:12, padding:'16px 24px', borderLeft:`5px solid ${sc.bg}` }}>
            <div style={{ fontSize:20, fontWeight:800, color:sc.bg, minWidth:28 }}>{i + 1}.</div>
            <div style={{ fontSize:22, color:'#1F2937', lineHeight:1.5 }}>{typeof q === 'string' ? q : q.question}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideVocabContext({ slide }) {
  const { items, sc, title } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'50px 80px' }}>
      <div style={{ fontSize:13, fontWeight:700, color:sc.bg, textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:22, fontWeight:700, color:'#0D1B2A', marginBottom:28 }}>🔍 Vocabulary in Context</div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:10 }}>
        {items.map((item, i) => (
          <div key={i} style={{ background:sc.light, borderRadius:12, padding:'14px 24px', borderLeft:`5px solid ${sc.bg}` }}>
            <span style={{ fontWeight:800, fontSize:20, color:'#0D1B2A' }}>{item.word || item}</span>
            {item.context && <span style={{ fontSize:18, color:'#6B7280', marginLeft:12 }}>— {item.context}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideWrapup({ slide }) {
  const { mat, sc } = slide;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:24, textAlign:'center', padding:'0 60px' }}>
      <div style={{ fontSize:60 }}>🎉</div>
      <div style={{ fontSize:40, fontWeight:800, color:'#0D1B2A' }}>Lesson Complete!</div>
      <div style={{ fontSize:20, color:'#6B7280', maxWidth:500, lineHeight:1.6 }}>
        Great work covering <strong>{mat.title}</strong>.<br />
        {mat.skill_type === 'grammar' && 'Practice these structures in your next conversation.'}
        {mat.skill_type === 'vocabulary' && 'Try using these words in sentences today.'}
        {mat.skill_type === 'writing' && 'Complete the writing task before the next session.'}
        {mat.skill_type === 'reading' && 'Read the passage again at home for deeper understanding.'}
      </div>
      {mat.student_tip && (
        <div style={{ background:'#FEF9C3', borderRadius:16, padding:'20px 32px', maxWidth:600, fontSize:18, color:'#78350F', lineHeight:1.6 }}>
          <strong>💡 Student Tip:</strong> {mat.student_tip}
        </div>
      )}
    </div>
  );
}

// ─── Slide dispatcher ─────────────────────────────────────────────────────────
function RenderSlide({ slide, showAnswer, onToggleAnswer }) {
  switch (slide.type) {
    case 'title':         return <SlideTitle slide={slide} />;
    case 'explanation':   return <SlideExplanation slide={slide} />;
    case 'mistakes':      return <SlideMistakes slide={slide} />;
    case 'examples':      return <SlideExamples slide={slide} />;
    case 'exercise':      return <SlideExercise slide={slide} showAnswer={showAnswer} onToggleAnswer={onToggleAnswer} />;
    case 'word':          return <SlideWord slide={slide} />;
    case 'writing_prompt':return <SlideWritingPrompt slide={slide} />;
    case 'model_text':    return <SlideModelText slide={slide} />;
    case 'phrases':       return <SlidePhrases slide={slide} />;
    case 'passage':       return <SlidePassage slide={slide} />;
    case 'comprehension': return <SlideComprehension slide={slide} />;
    case 'vocab_context': return <SlideVocabContext slide={slide} />;
    case 'wrapup':        return <SlideWrapup slide={slide} />;
    default:              return <div style={{ padding:40, fontSize:20 }}>Unknown slide type: {slide.type}</div>;
  }
}

// ─── Slide thumbnail strip ────────────────────────────────────────────────────
const SLIDE_ICON = {
  title: '🏷️', explanation: '📖', mistakes: '⚠️', examples: '✏️',
  exercise: '🧩', word: '📚', writing_prompt: '✍️', model_text: '📝',
  phrases: '💬', passage: '📄', comprehension: '❓', vocab_context: '🔍', wrapup: '🎉',
};
const SLIDE_LABEL = {
  title: 'Title', explanation: 'Explanation', mistakes: 'Mistakes', examples: 'Examples',
  exercise: 'Exercise', word: 'Word', writing_prompt: 'Task', model_text: 'Model',
  phrases: 'Phrases', passage: 'Passage', comprehension: 'Questions', vocab_context: 'Vocab', wrapup: 'Done',
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function LessonPresent() {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showNav, setShowNav] = useState(true);

  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    api.get(`/api/tutor/lesson-materials/${materialId}`)
      .then(r => {
        const mat = r.data.material;
        setMaterial(mat);
        if (mat.file_url) {
          api.get(mat.file_url, { responseType: 'blob' })
            .then(res => { setBlobUrl(URL.createObjectURL(res.data)); })
            .catch(() => setBlobUrl('error'));
        } else {
          setSlides(buildSlides(mat));
        }
      })
      .catch(() => setError('Could not load lesson material.'))
      .finally(() => setLoading(false));
  }, [materialId]);

  const go = useCallback((dir) => {
    setCurrent(c => Math.max(0, Math.min(slides.length - 1, c + dir)));
    setShowAnswer(false);
  }, [slides.length]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')                   { e.preventDefault(); go(-1); }
      if (e.key === 'Escape') navigate(-1);
      if (e.key === 'a' || e.key === 'A') setShowAnswer(s => !s);
      if (e.key === 'h' || e.key === 'H') setShowNav(s => !s);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [go, navigate]);

  if (loading) return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0D1B2A' }}>
      <div style={{ color:'#C9A84C', fontSize:20, fontWeight:700 }}>Loading lesson…</div>
    </div>
  );
  if (error || !material) return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0D1B2A' }}>
      <div style={{ color:'#EF4444', fontSize:18 }}>{error || 'Not found'}</div>
    </div>
  );

  // File-based presentation (PDF or Office file)
  if (material && material.file_url) {
    const isPdf = material.file_type === 'pdf';
    return (
      <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:'#0D1B2A', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', gap:14, padding:'10px 20px', background:'#0D1B2A', flexShrink:0, borderBottom:'1px solid #1F2937' }}>
          <button onClick={() => navigate(-1)} style={{ background:'transparent', border:'1px solid #374151', color:'#9CA3AF', padding:'5px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
            Back
          </button>
          <span style={{ fontSize:13, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#1D4ED8', color:'#fff' }}>{material.skill_type}</span>
          <span style={{ fontSize:13, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#C9A84C', color:'#0D1B2A' }}>{material.cefr_level}</span>
          <span style={{ fontSize:15, fontWeight:700, color:'#F9FAFB', flex:1 }}>{material.title}</span>
          <span style={{ fontSize:12, color:'#9CA3AF' }}>Share your screen on Google Meet</span>
          {blobUrl && blobUrl !== 'error' && (
            <a href={blobUrl} download={material.file_name}
              style={{ padding:'5px 14px', background:'#374151', color:'#F9FAFB', borderRadius:6, fontSize:12, textDecoration:'none', fontWeight:600 }}>
              Download
            </a>
          )}
        </div>
        <div style={{ flex:1, overflow:'hidden', background:'#111827' }}>
          {blobUrl === null && (
            <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ color:'#9CA3AF', fontSize:15 }}>Loading file...</div>
            </div>
          )}
          {blobUrl === 'error' && (
            <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, padding:40, textAlign:'center' }}>
              <div style={{ color:'#F9FAFB', fontSize:20, fontWeight:700 }}>{material.title}</div>
              <div style={{ color:'#9CA3AF', fontSize:14 }}>{material.file_name}</div>
              <div style={{ color:'#6B7280', fontSize:13, maxWidth:420 }}>
                Could not preview in browser. Download the file, open it on your computer, then share your screen on Google Meet.
              </div>
              <a href={material.file_url} target="_blank" rel="noopener noreferrer"
                style={{ padding:'10px 24px', background:'#00B4D8', color:'#fff', borderRadius:10, fontWeight:700, fontSize:15, textDecoration:'none', marginTop:8 }}>
                Open file
              </a>
            </div>
          )}
          {blobUrl && blobUrl !== 'error' && isPdf && (
            <iframe src={blobUrl} title={material.title}
              style={{ width:'100%', height:'100%', border:'none', background:'#fff' }} />
          )}
          {blobUrl && blobUrl !== 'error' && !isPdf && (
            <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:20, padding:40, textAlign:'center' }}>
              <div style={{ fontSize:64 }}>{material.file_type && material.file_type.includes('ppt') ? '📊' : material.file_type && material.file_type.includes('xls') ? '📋' : '📝'}</div>
              <div style={{ color:'#F9FAFB', fontSize:22, fontWeight:800 }}>{material.title}</div>
              <div style={{ color:'#9CA3AF', fontSize:14 }}>{material.file_name}</div>
              <div style={{ color:'#6B7280', fontSize:13, maxWidth:460, lineHeight:1.7 }}>
                Word, PowerPoint and Excel files cannot be previewed in the browser.<br/>
                Download the file, open it on your computer, then share your screen on Google Meet.
              </div>
              <a href={blobUrl} download={material.file_name}
                style={{ padding:'12px 32px', background:'#00B4D8', color:'#fff', borderRadius:10, fontWeight:800, fontSize:16, textDecoration:'none', marginTop:4 }}>
                Download and Open
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  const slide = slides[current];
  const sc = slide.sc;
  const progress = slides.length > 1 ? (current / (slides.length - 1)) * 100 : 100;

  return (
    <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:'#FFFFFF', fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflow:'hidden' }}>

      {/* ── Top bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'10px 20px', background:'#0D1B2A', flexShrink:0 }}>
        <button onClick={() => navigate(-1)} style={{ background:'transparent', border:'1px solid #374151', color:'#9CA3AF', padding:'5px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
          ← Back
        </button>
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:13, fontWeight:700, padding:'3px 10px', borderRadius:20, background:sc.bg, color:'#fff' }}>{material.skill_type}</span>
          <span style={{ fontSize:13, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#C9A84C', color:'#0D1B2A' }}>{material.cefr_level}</span>
          <span style={{ fontSize:15, fontWeight:700, color:'#F9FAFB' }}>{material.title}</span>
        </div>
        <div style={{ fontSize:13, color:'#9CA3AF' }}>{current + 1} / {slides.length}</div>
        <button
          onClick={() => setShowNav(s => !s)}
          style={{ background:'transparent', border:'1px solid #374151', color:'#9CA3AF', padding:'5px 12px', borderRadius:6, fontSize:12, cursor:'pointer' }}>
          {showNav ? 'Hide Nav' : 'Show Nav'} [H]
        </button>
      </div>

      {/* ── Progress bar ── */}
      <div style={{ height:4, background:'#E5E7EB', flexShrink:0 }}>
        <div style={{ height:'100%', width:`${progress}%`, background:sc.bg, transition:'width 0.3s ease' }} />
      </div>

      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* ── Slide thumbnail strip ── */}
        {showNav && (
          <div style={{ width:160, background:'#F9FAFB', borderRight:'1px solid #E5E7EB', overflowY:'auto', flexShrink:0, padding:'8px 6px', display:'flex', flexDirection:'column', gap:4 }}>
            {slides.map((s, i) => (
              <button key={i} onClick={() => { setCurrent(i); setShowAnswer(false); }}
                style={{
                  display:'flex', alignItems:'center', gap:8, padding:'7px 10px',
                  borderRadius:8, border:'none', cursor:'pointer', textAlign:'left',
                  background: i === current ? sc.bg : 'transparent',
                  color: i === current ? '#fff' : '#374151',
                  fontWeight: i === current ? 700 : 400,
                  fontSize:12,
                }}>
                <span style={{ fontSize:15 }}>{SLIDE_ICON[s.type] || '•'}</span>
                <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {s.type === 'exercise' ? `Q${s.idx + 1}` : s.type === 'word' ? (s.word?.word || `Word ${s.idx+1}`) : SLIDE_LABEL[s.type]}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── Main slide area ── */}
        <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', inset:0, overflowY:'auto' }}>
            <RenderSlide slide={slide} showAnswer={showAnswer} onToggleAnswer={() => setShowAnswer(s => !s)} />
          </div>
        </div>
      </div>

      {/* ── Bottom navigation ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, padding:'12px 20px', background:'#F9FAFB', borderTop:'1px solid #E5E7EB', flexShrink:0 }}>
        <button
          onClick={() => go(-1)} disabled={current === 0}
          style={{ padding:'10px 28px', borderRadius:10, border:'2px solid #E5E7EB', background:current === 0 ? '#F3F4F6' : '#fff', color:current === 0 ? '#9CA3AF' : '#374151', fontWeight:700, fontSize:15, cursor:current === 0 ? 'not-allowed' : 'pointer' }}>
          ← Previous
        </button>
        <div style={{ display:'flex', gap:6 }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => { setCurrent(i); setShowAnswer(false); }} style={{
              width: i === current ? 24 : 8, height:8, borderRadius:4,
              background: i === current ? sc.bg : '#D1D5DB',
              cursor:'pointer', transition:'width 0.2s ease',
            }} />
          ))}
        </div>
        <button
          onClick={() => go(1)} disabled={current === slides.length - 1}
          style={{ padding:'10px 28px', borderRadius:10, border:'none', background:current === slides.length - 1 ? '#9CA3AF' : sc.bg, color:'#fff', fontWeight:700, fontSize:15, cursor:current === slides.length - 1 ? 'not-allowed' : 'pointer' }}>
          Next →
        </button>
      </div>

      {/* ── Keyboard hint ── */}
      <div style={{ textAlign:'center', padding:'6px', fontSize:11, color:'#9CA3AF', background:'#F9FAFB', borderTop:'1px solid #F3F4F6' }}>
        ← → Navigate &nbsp;·&nbsp; [A] Toggle answer &nbsp;·&nbsp; [H] Hide nav &nbsp;·&nbsp; [Esc] Exit
      </div>
    </div>
  );
}
